import React, { useEffect, useState } from "react";
import { ScrollText, MessageSquare, ListChecks, Timer, MapPin, X, ArrowRight } from "lucide-react";
import { setWalkthroughDone, isWalkthroughDone } from "../lib/appFeedback";

const STEPS = [
  {
    key: "notes",
    icon: ScrollText,
    color: "text-brimi-navy bg-brimi-navy/10",
    title: "Start with Chapter Notes",
    body:
      "Open any chapter to read AI-generated notes in your own words. Use Read Aloud when you're on the go, and download the PDF to revise offline.",
  },
  {
    key: "chat",
    icon: MessageSquare,
    color: "text-brimi-terracotta bg-brimi-terracotta/15",
    title: "Ask the AI Tutor anything",
    body:
      "Stuck on a concept? Tap the chat bubble on any chapter and ask in plain English (or with your voice). The tutor explains at your level.",
  },
  {
    key: "mcq",
    icon: ListChecks,
    color: "text-brimi-sage bg-brimi-sage/15",
    title: "Practice with Chapter MCQs",
    body:
      "Every chapter has 10 fresh MCQs with instant scoring. Perfect for a 5-minute revision after reading.",
  },
  {
    key: "mock",
    icon: Timer,
    color: "text-brimi-terracotta bg-brimi-terracotta/15",
    title: "Take the 20-Question Mock Test",
    body:
      "A timed, mixed test across the whole syllabus with a chapter-wise breakdown at the end. Do at least one before your boards.",
  },
  {
    key: "map",
    icon: MapPin,
    color: "text-brimi-sage bg-brimi-sage/15",
    title: "Master Map Work — India & World",
    body:
      "Tap-to-place quiz on real GeoJSON outlines. Great for the Geography 20-mark map question.",
  },
];

export default function Walkthrough({ forceOpen = false, onClose }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (forceOpen) {
      setI(0);
      setOpen(true);
      return;
    }
    if (!isWalkthroughDone()) {
      // slight delay so the dashboard has time to render first
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [forceOpen]);

  const finish = () => {
    setWalkthroughDone();
    setOpen(false);
    if (onClose) onClose();
  };

  if (!open) return null;
  const step = STEPS[i];
  const Icon = step.icon;
  const isLast = i === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[80] bg-brimi-navyText/50 backdrop-blur-[2px] flex items-end sm:items-center justify-center p-4"
      data-testid="walkthrough-modal"
    >
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-5">
          <span className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/55">
            Quick tour · {i + 1} of {STEPS.length}
          </span>
          <button
            onClick={finish}
            className="text-brimi-navyText/50 hover:text-brimi-navyText p-1"
            data-testid="walkthrough-skip-btn"
            aria-label="Skip walkthrough"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pt-6 pb-4 text-center">
          <div className={`w-16 h-16 grid place-items-center rounded-2xl mx-auto ${step.color}`}>
            <Icon size={28} />
          </div>
          <h2 className="mt-4 text-2xl font-heading font-bold text-brimi-navyText">{step.title}</h2>
          <p className="mt-2 text-brimi-navyText/70 text-sm leading-relaxed">{step.body}</p>
        </div>

        <div className="px-5 pb-2 flex items-center justify-center gap-1.5">
          {STEPS.map((_, idx) => (
            <span
              key={idx}
              className={`h-1.5 rounded-full transition-all ${
                idx === i ? "w-6 bg-brimi-navy" : "w-1.5 bg-brimi-creamSoft"
              }`}
            />
          ))}
        </div>

        <div className="px-5 pb-5 pt-3 flex items-center justify-between gap-3">
          <button
            onClick={finish}
            className="text-sm font-semibold text-brimi-navyText/60 hover:text-brimi-navyText px-3 py-2"
            data-testid="walkthrough-skip-text-btn"
          >
            Skip
          </button>
          {!isLast ? (
            <button
              onClick={() => setI((v) => v + 1)}
              className="brimi-btn-primary"
              data-testid="walkthrough-next-btn"
            >
              Next <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={finish}
              className="brimi-btn-primary"
              data-testid="walkthrough-done-btn"
            >
              Let&apos;s go <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
